/**
 * Generic Community Treasury Section for City Landing Pages
 *
 * Explains how everyday spending at verified vendors funds community projects
 * through a resident-controlled treasury. Fully parameterized for any city.
 *
 * Usage:
 *   <CommunityTreasurySection
 *     cityName="Houston"
 *     stateName="Texas"
 *     annualSpending="$120B"
 *     projectedTreasury="$8M-$12M"
 *     vendorsPageUrl="/vendors"
 *   />
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Wallet,
  TrendingUp,
  Users,
  Building2,
  PiggyBank,
  Landmark,
  ShoppingCart,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';

export interface CommunityTreasurySectionProps {
  cityName: string;
  stateName?: string;
  annualSpending: string; // e.g., "$200-300B"
  vendorsPageUrl?: string; // Link to vendors page
}

export function CommunityTreasurySection({
  cityName,
  stateName = '',
  annualSpending,
  vendorsPageUrl = '/vendors',
}: CommunityTreasurySectionProps) {
  // Calculate treasury: 0.2% of annual spending at 100% adoption
  const projectedTreasury = React.useMemo(() => {
    // Parse spending range (e.g., "$200-300B" -> [200, 300])
    const match = annualSpending.match(/\$(\d+)(?:-(\d+))?([BMK])/i);
    if (!match) return annualSpending; // fallback if format doesn't match

    const [_, low, high, unit] = match;
    const multiplier =
      unit === 'B' ? 1_000_000_000 : unit === 'M' ? 1_000_000 : unit === 'K' ? 1_000 : 1;

    const lowValue = parseFloat(low) * multiplier * 0.002; // 0.2%
    const highValue = (high ? parseFloat(high) : parseFloat(low)) * multiplier * 0.002; // 0.2%

    // Format back to string
    const formatValue = (val: number) => {
      if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1)}B`;
      if (val >= 1_000_000) return `$${Math.round(val / 1_000_000)}M`;
      return `$${Math.round(val / 1_000)}K`;
    };

    return `${formatValue(lowValue)}${highValue !== lowValue ? `-${formatValue(highValue)}` : ''}`;
  }, [annualSpending]);

  // Calculate city/treasury revenue at 30% adoption scenario
  const adoptionScenarioRevenue = React.useMemo(() => {
    // Parse spending range (e.g., "$200-300B" -> [200, 300])
    const match = annualSpending.match(/\$(\d+)(?:-(\d+))?([BMK])/i);
    if (!match) return annualSpending; // fallback if format doesn't match

    const [_, low, high, unit] = match;
    const multiplier =
      unit === 'B' ? 1_000_000_000 : unit === 'M' ? 1_000_000 : unit === 'K' ? 1_000 : 1;

    // At 30% adoption
    const adoptionRate = 0.3;
    const lowValue = parseFloat(low) * multiplier * adoptionRate * 0.002; // 30% * 0.2%
    const highValue =
      (high ? parseFloat(high) : parseFloat(low)) * multiplier * adoptionRate * 0.002;

    // Format back to string
    const formatValue = (val: number) => {
      if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1)}B`;
      if (val >= 1_000_000) return `$${Math.round(val / 1_000_000)}M`;
      return `$${Math.round(val / 1_000)}K`;
    };

    return `${formatValue(lowValue)}${highValue !== lowValue ? `-${formatValue(highValue)}` : ''}`;
  }, [annualSpending]);

  const scrollToVendors = () => {
    window.location.href = vendorsPageUrl;
  };

  return (
    <section
      id="community-treasury"
      data-nav-title="Community Treasury"
      className="py-16 sm:py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-background"
    >
      <div className="container px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              Community Wealth Building
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Your Everyday Spending Funds Community Projects
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {cityName} residents spend{' '}
              <span className="font-semibold text-primary">{annualSpending} per year</span> at local
              businesses (groceries, restaurants, gas, services - not including industrial plants).
              When that spending flows through verified vendors, a tiny portion builds a
              <span className="font-semibold text-primary"> resident-controlled fund</span> for
              parks, sidewalks, and community projects. You vote. You decide. No waiting for someone
              to donate or city to fund it.
            </p>
          </div>

          {/* Simple Math Visual */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 mb-12 border-2 border-primary/20">
            <h3 className="text-2xl font-bold text-center mb-6">The Math Is Simple</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-primary mb-2">
                  {annualSpending}
                </div>
                <p className="text-sm text-muted-foreground">
                  What {cityName} residents spend per year
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  (groceries, restaurants, gas, services - excludes industrial plants)
                </p>
              </div>

              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="text-xl font-semibold text-muted-foreground mb-2"></div>
                  <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">1/3</div>
                  <p className="text-sm text-muted-foreground">Uses verified vendors</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    (~$2B+ flows through platform)
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-green-600 mb-2">
                  {projectedTreasury}
                </div>
                <p className="text-sm font-semibold text-foreground">Annual Community Treasury</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Resident-controlled infrastructure fund
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
                Every time you buy groceries, eat at a restaurant, or hire a verified plumber, a
                tiny fee (you never see it) goes to the Community Treasury. It adds up fast.{' '}
                <span className="font-semibold text-foreground">
                  If just 1 in 3 purchases use verified vendors, {cityName} gets {projectedTreasury}
                  /year for community projects.
                </span>
              </p>
            </div>
          </div>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="border-2 border-primary/20 bg-background/50 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <Wallet className="w-12 h-12 mx-auto text-primary mb-3" />
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">
                  {projectedTreasury}
                </div>
                <p className="text-sm text-muted-foreground">Projected Annual Community Treasury</p>
                <p className="text-xs text-muted-foreground mt-1">
                  (if 1 in 3 purchases use verified vendors)
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/20 bg-background/50 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <PiggyBank className="w-12 h-12 mx-auto text-primary mb-3" />
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">Tiny Fee</div>
                <p className="text-sm text-muted-foreground">
                  Small transaction fee (invisible to you)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Vendors process it - you never see it
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/20 bg-background/50 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <Users className="w-12 h-12 mx-auto text-primary mb-3" />
                <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">You Vote</div>
                <p className="text-sm text-muted-foreground">Democratic Governance</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Residents decide how to spend every dollar
                </p>
              </CardContent>
            </Card>
          </div>

          {/* How It Works */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center">
              How the Community Treasury Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <ShoppingCart className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Step 1: Shop Like Normal</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        You buy groceries, eat at restaurants, hire a plumber, get your oil changed
                        -{' '}
                        <span className="font-semibold text-foreground">just like you do now</span>.
                        The only difference: you choose{' '}
                        <span className="font-semibold text-primary">verified vendors</span> listed
                        in the Community App. You pay the same price. Nothing changes for you.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-2">
                        Step 2: A Tiny Fee Builds the Treasury
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Behind the scenes, verified vendors pay a small fee when you shop with them.
                        <span className="font-semibold text-foreground">
                          {' '}
                          You never see it. You never pay it.
                        </span>{' '}
                        It's invisible to you. Part of that fee goes to the Community Treasury, part
                        goes to the City of {cityName}, and part keeps the platform running. Think
                        of it like sales tax - except{' '}
                        <span className="font-semibold text-primary">
                          residents control how it's spent
                        </span>
                        , not city council.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <Wallet className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-2">
                        Step 3: You Vote on What to Build
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        The Community Treasury grows every time people shop through verified
                        vendors. When there's enough money,
                        <span className="font-semibold text-primary"> residents vote</span> on what
                        to build: new playground? Fix the sidewalks? Renovate the park? Whatever
                        wins the vote gets funded.
                        <span className="font-semibold text-foreground">
                          {' '}
                          No city council. No waiting. You decide.
                        </span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3 flex-shrink-0">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold mb-2">
                        Step 4: Money Stays in {cityName}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        When the community votes to build something, that contract goes to a
                        <span className="font-semibold text-primary"> verified local business</span>
                        . The park renovation? Hired a local contractor. The sidewalk repairs? Hired
                        a local crew. The money you spent shopping at verified vendors
                        <span className="font-semibold text-foreground">
                          {' '}
                          comes back to local businesses
                        </span>
                        . It never leaves {cityName}.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Example Projects */}
          <div className="bg-muted/50 rounded-xl p-6 sm:p-8 border-2 border-primary/10 mb-12">
            <h3 className="text-xl font-bold mb-4 text-center">
              What {projectedTreasury}/Year Can Fund
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
              These are illustrative examples of community projects that could be funded by the
              Community Treasury at 30% market adoption. Actual spending is determined by resident
              votes.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Neighborhood Park Renovations</p>
                  <p className="text-xs text-muted-foreground">$50K-$150K per park</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Community Center Upgrades</p>
                  <p className="text-xs text-muted-foreground">$200K-$500K</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Sidewalk/Road Repairs</p>
                  <p className="text-xs text-muted-foreground">$100K-$300K</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Public Art Installations</p>
                  <p className="text-xs text-muted-foreground">$20K-$80K</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Youth Sports Facilities</p>
                  <p className="text-xs text-muted-foreground">$150K-$400K</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Community Event Funding</p>
                  <p className="text-xs text-muted-foreground">$30K-$100K/year</p>
                </div>
              </div>
            </div>
          </div>

          {/* City Benefits */}
          <Card className="border-2 border-primary/20 mb-12">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Landmark className="w-12 h-12 text-primary flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-3">The City Gets Revenue Too</h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    The transaction fee is split three ways:{' '}
                    <span className="font-semibold text-foreground">
                      Community Treasury gets its own share
                    </span>
                    ,
                    <span className="font-semibold text-foreground">
                      the City of {cityName} gets a separate share
                    </span>
                    , and the platform gets its operating share. These are independent streams - the
                    city's revenue doesn't come from the Community Treasury, they each get their own
                    portion. If 1 in 3 purchases use verified vendors (30% adoption), the city
                    receives{' '}
                    <span className="font-semibold text-primary">
                      {adoptionScenarioRevenue}/year
                    </span>{' '}
                    while the Community Treasury receives{' '}
                    <span className="font-semibold text-primary">
                      {adoptionScenarioRevenue}/year separately
                    </span>
                    .
                    <span className="font-semibold text-foreground">
                      {' '}
                      Zero budget allocation. Zero tax increase.
                    </span>{' '}
                    The more people use the platform, the more both the city and Community Treasury
                    earn.
                  </p>
                  <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Translation:</span> The city
                      can fund new projects (street repairs, public safety, economic development)
                      without raising taxes or cutting other programs. It's performance-based
                      revenue -
                      <span className="font-semibold text-foreground">
                        {' '}
                        if residents use the platform, the city gets paid. If not, they don't.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Verified Vendors Required */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 sm:p-8 border-2 border-amber-300 shadow-sm mb-8">
            <h3 className="text-xl font-bold text-amber-900 mb-3 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Why Vendors Join the Verified Network
            </h3>
            <p className="text-amber-800 text-sm sm:text-base leading-relaxed mb-4">
              The Community Treasury only grows when residents shop at{' '}
              <span className="font-semibold">verified vendors</span> in the app. Non-verified
              businesses can't participate. Here's why local vendors join:
            </p>
            <ul className="space-y-2 text-amber-800 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <span className="font-semibold">Competitive Payment Processing Rates:</span> When
                  hundreds of vendors process payments together through the same network, we
                  negotiate better bulk rates than individual businesses could get alone (same
                  concept as Costco buying power, but for credit card fees)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <span className="font-semibold">Community Projects Hire Local Vendors:</span> When
                  residents vote to fund a $150K park renovation from the Community Treasury,
                  <span className="font-semibold"> verified vendors can bid on that contract</span>.
                  Your business processes $100K in customer transactions this year → contributes to
                  treasury growth → next year you could win a $50K landscaping contract funded by
                  that same treasury. The money cycles back.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <span className="font-semibold">Residents Prefer Verified Vendors:</span> When
                  people know their everyday spending at your business helps fund community projects
                  they get to vote on (like new playgrounds or sidewalk repairs), that's a
                  compelling reason to choose you over competitors who aren't part of the verified
                  network
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <span className="font-semibold">City Actively Promotes Verified Vendors:</span>{' '}
                  {cityName} earns municipal revenue when residents shop at verified businesses,
                  giving the city a financial incentive to promote your business through official
                  channels (something they'd never do for Yelp listings or generic directories)
                </span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-6">Bottom Line: Everyone Wins</h3>
            <div className="flex justify-center mb-6">
              <ul className="space-y-3 text-left inline-block">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">Residents</span> get money for
                    community projects
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">City</span> gets new revenue
                    without raising taxes
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">Vendors</span> get customers and
                    treasury-funded contracts
                  </span>
                </li>
              </ul>
            </div>
            <p className="text-muted-foreground mb-6">
              <span className="font-semibold text-primary">All powered by everyday spending</span>{' '}
              you're already doing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={scrollToVendors} className="gap-2">
                Learn More About Verified Vendors <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  const championsSection = document.getElementById('champions');
                  if (championsSection) championsSection.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Become a Champion
              </Button>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              <span className="font-semibold">Important:</span> The Community Treasury model is our
              intended framework, but implementation depends on legal structuring, city partnership
              agreements, and sufficient vendor/resident adoption. Projected revenue figures are
              illustrative based on economic modeling - actual results depend on market penetration
              and transaction volumes. Community Treasury governance structure (voting procedures,
              spending authority, oversight) will be defined during platform deployment in
              coordination with legal counsel and city officials. This is not yet active - we're
              gathering community interest to determine feasibility.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
